// Multiplayer screen sharing over the existing LiveKit room (same connection
// as voice). The presenter publishes a screen-share video track; every other
// participant receives it and it lands in worldStore.activeScreenShare, which
// all 'screen-share' props render as a live video texture — including in VR.

import { AudioPresets, Room, RoomEvent, Track, RemoteTrack } from 'livekit-client';
import { useWorldStore } from '../stores/worldStore';
import { broadcastScreenLive } from './worldBroadcast';
import { getCurrentWorldId } from './realtimeManager';

let currentRoom: Room | null = null;
let shareAudioEls: HTMLMediaElement[] = [];
let removeRoomListeners: (() => void) | null = null;

function handleRemoteTrack(track: RemoteTrack) {
  if (track.source === Track.Source.ScreenShare && track.kind === 'video') {
    useWorldStore.getState().setActiveScreenShare(new MediaStream([track.mediaStreamTrack]));
  } else if (track.source === Track.Source.ScreenShareAudio && track.kind === 'audio') {
    // Play the presenter's shared audio (tab/system sound). The presenter
    // never receives their own track back, so there's no echo.
    const el = track.attach();
    el.style.display = 'none';
    document.body.appendChild(el);
    (el as HTMLAudioElement).play?.().catch(() => { /* resumes on next user gesture */ });
    shareAudioEls.push(el);
  }
}

function removeShareAudio(track: RemoteTrack) {
  track.detach().forEach((el) => el.remove());
  shareAudioEls = shareAudioEls.filter((el) => el.isConnected);
}

export function attachScreenShareToRoom(room: Room) {
  detachScreenShareRoom();
  currentRoom = room;
  // Pick up any share already in progress when we join
  room.remoteParticipants.forEach((p) => {
    p.trackPublications.forEach((pub) => {
      if ((pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio) && pub.track) {
        handleRemoteTrack(pub.track as RemoteTrack);
      }
    });
  });
  const subscribed = (track: RemoteTrack) => handleRemoteTrack(track);
  const unsubscribed = (track: RemoteTrack) => {
    if (track.source === Track.Source.ScreenShare) {
      const active = useWorldStore.getState().activeScreenShare;
      if (active?.getVideoTracks().some(t => t.id === track.mediaStreamTrack.id)) useWorldStore.getState().setActiveScreenShare(null);
    }
    if (track.source === Track.Source.ScreenShareAudio) removeShareAudio(track);
  };
  const unpublished = (pub: { source: Track.Source }) => {
    if (pub.source === Track.Source.ScreenShare) useWorldStore.getState().setActiveScreenShare(null);
  };
  room.on(RoomEvent.TrackSubscribed, subscribed);
  room.on(RoomEvent.TrackUnsubscribed, unsubscribed);
  room.on(RoomEvent.LocalTrackUnpublished, unpublished);
  removeRoomListeners = () => {
    room.off(RoomEvent.TrackSubscribed, subscribed);
    room.off(RoomEvent.TrackUnsubscribed, unsubscribed);
    room.off(RoomEvent.LocalTrackUnpublished, unpublished);
  };
}

export function detachScreenShareRoom(room?: Room) {
  if (room && room !== currentRoom) return;
  removeRoomListeners?.(); removeRoomListeners = null;
  shareAudioEls.forEach(el => { el.pause(); el.srcObject = null; el.remove(); });
  shareAudioEls = [];
  currentRoom = null;
  useWorldStore.getState().setActiveScreenShare(null);
}

/** Resolves once the voice room is attached (joining it muted if needed). */
function ensureRoom(timeoutMs = 20000): Promise<Room> {
  if (currentRoom) return Promise.resolve(currentRoom);
  window.dispatchEvent(new CustomEvent('gathrxr:voice-join'));
  return new Promise((resolve, reject) => {
    const t0 = Date.now();
    const tick = () => {
      if (currentRoom) return resolve(currentRoom);
      if (Date.now() - t0 > timeoutMs) return reject(new Error('Could not connect to the room. Check your connection and try again.'));
      setTimeout(tick, 200);
    };
    tick();
  });
}

// While presenting, tell the room every few seconds so people who haven't
// joined voice (or arrive later) connect to watch.
let liveTimer: ReturnType<typeof setInterval> | null = null;
function announceLive(on: boolean) {
  if (liveTimer) { clearInterval(liveTimer); liveTimer = null; }
  const send = () => {
    const worldId = useWorldStore.getState().actualWorldId || getCurrentWorldId();
    if (worldId) broadcastScreenLive(worldId, on);
  };
  send();
  if (on) liveTimer = setInterval(send, 8000);
}

/**
 * Start presenting: browser share picker → publish to the room → return the
 * local stream for immediate preview. The picker opens first, while the click
 * still counts as a user gesture; the voice room is joined (muted) after if
 * this player hadn't joined yet.
 */
export async function startScreenShare(): Promise<MediaStream> {
  if (currentRoom?.localParticipant.isScreenShareEnabled) throw new Error('You are already sharing.');
  if (useWorldStore.getState().activeScreenShare) {
    throw new Error('Someone is already sharing. Wait for them to stop, or place a screen to watch their share.');
  }
  // Tuned for video (YouTube) rather than slides: 30 fps, and tab audio with
  // the voice processing (echo cancel, noise suppression, gain) turned off so
  // music isn't muffled. Chrome offers a "share tab audio" checkbox for tabs.
  const captured = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: 30, max: 30 }, width: { max: 1920 }, height: { max: 1080 } },
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
  } as DisplayMediaStreamOptions);
  const [video] = captured.getVideoTracks();
  const audio = captured.getAudioTracks()[0];
  try { video.contentHint = 'motion'; } catch { /* older browsers */ }
  let room: Room;
  try {
    room = await ensureRoom();
    if (useWorldStore.getState().activeScreenShare) throw new Error('Someone else started sharing first.');
    // LiveKit's screen-share default (~15 fps, sharpness first) suits slides;
    // keep the frame rate up instead and let resolution drop when bandwidth dips.
    await room.localParticipant.publishTrack(video, {
      source: Track.Source.ScreenShare,
      videoEncoding: { maxBitrate: 4_000_000, maxFramerate: 30 },
      degradationPreference: 'maintain-framerate',
      simulcast: false,
    });
    if (audio) await room.localParticipant.publishTrack(audio, {
      source: Track.Source.ScreenShareAudio,
      audioPreset: AudioPresets.musicHighQualityStereo,
      forceStereo: true,
      dtx: false,
    });
  } catch (err) {
    captured.getTracks().forEach((t) => t.stop());
    throw err;
  }
  // The browser's own "Stop sharing" bar ends the track: unpublish then.
  video.addEventListener('ended', () => { void stopScreenShare(); });
  const stream = new MediaStream([video]);
  useWorldStore.getState().setActiveScreenShare(stream);
  announceLive(true);
  return stream;
}

export async function stopScreenShare(): Promise<void> {
  announceLive(false);
  const room = currentRoom;
  if (room) {
    for (const pub of [...room.localParticipant.trackPublications.values()]) {
      if (pub.source !== Track.Source.ScreenShare && pub.source !== Track.Source.ScreenShareAudio) continue;
      const track = pub.track;
      try { if (track) await room.localParticipant.unpublishTrack(track); } catch { /* already gone */ }
      track?.mediaStreamTrack?.stop();
    }
  }
  const mine = useWorldStore.getState().activeScreenShare;
  if (mine && (!room || mine.getVideoTracks().every((t) => t.readyState === 'ended'))) useWorldStore.getState().setActiveScreenShare(null);
}

/** Someone announced a live share: join the room (muted) to receive it. */
export function onRemoteScreenLive(live: boolean) {
  if (live && !currentRoom) window.dispatchEvent(new CustomEvent('gathrxr:voice-join'));
}

/** Can this device pick a screen/tab to share? Desktop Chrome, Edge, Firefox
 *  and Safari can; phones and headset browsers generally can't (they can still
 *  watch someone else's share). */
export function canCaptureScreen(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
}

export function isPresenting(): boolean {
  return !!currentRoom?.localParticipant.isScreenShareEnabled;
}
